import { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  useToast,
  Button,
  HStack,
  Input,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  VStack,
  Badge,
  Tooltip,
  Card,
  CardBody
} from '@chakra-ui/react';
import { RepeatIcon, EditIcon } from '@chakra-ui/icons';
import { getPositions, updatePosition } from '../api';

// 格式化数字
const formatNumber = (num) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const Positions = () => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [editValues, setEditValues] = useState({
    first_stop_loss_price: ''
  });
  const toast = useToast();

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const data = await getPositions();
      setPositions(data);
    } catch (error) {
      toast({
        title: '获取持仓失败',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, []);

  const handleRefresh = () => {
    if (!loading) {
      fetchPositions();
    }
  };

  const handleEdit = (position) => {
    if (loading) return;
    setEditingPosition(position);
    setEditValues({
      first_stop_loss_price: position.first_stop_loss_price || ''
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPosition(null);
    setEditValues({
      first_stop_loss_price: ''
    });
  };

  const handleSave = async () => {
    if (loading || !editingPosition) return;
    
    try {
      setLoading(true);
      await updatePosition(editingPosition.id, editValues);
      toast({
        title: '更新成功',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await fetchPositions();
      handleCloseModal();
    } catch (error) {
      toast({
        title: '更新失败',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardBody>
        <HStack mb={4} spacing={4} justify="space-between">
          <Text fontSize="xl" fontWeight="bold">持仓列表</Text>
          <Button
            leftIcon={<RepeatIcon />}
            onClick={handleRefresh}
            isLoading={loading}
            colorScheme="blue"
            variant="outline"
            size="sm"
          >
            刷新
          </Button>
        </HStack>

        {loading ? (
          <Spinner />
        ) : positions.length === 0 ? (
          <Text>暂无持仓</Text>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>标的</Th>
                  <Th>数量</Th>
                  <Th isNumeric>持仓价格</Th>
                  <Th isNumeric>未实现盈亏</Th>
                  <Th isNumeric>止损价格</Th>
                  <Th>建仓时间</Th>
                  <Th>最后更新</Th>
                  <Th>操作</Th>
                </Tr>
              </Thead>
              <Tbody>
                {positions.map((position) => (
                  <Tr key={position.id}>
                    <Td>
                      <Badge colorScheme="purple" fontSize="sm">
                        {position.symbol}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={position.quantity > 0 ? 'green' : 'red'}
                        variant="subtle"
                        fontSize="sm"
                      >
                        {position.quantity > 0 ? '多' : '空'} {Math.abs(position.quantity)}
                      </Badge>
                    </Td>
                    <Td isNumeric>{formatNumber(position.price)}</Td>
                    <Td isNumeric>
                      <Badge
                        colorScheme={position.unrealized_pnl >= 0 ? 'green' : 'red'}
                        variant="outline"
                        fontSize="sm"
                      >
                        {formatNumber(position.unrealized_pnl)}
                      </Badge>
                    </Td>
                    <Td isNumeric>
                      <Tooltip label="止损价格" placement="top">
                        <Badge colorScheme="orange" variant="outline" fontSize="sm">
                          {formatNumber(position.first_stop_loss_price)}
                        </Badge>
                      </Tooltip>
                    </Td>
                    <Td fontSize="sm">{new Date(position.created_at).toLocaleString()}</Td>
                    <Td fontSize="sm">{new Date(position.updated_at).toLocaleString()}</Td>
                    <Td>
                      <Tooltip label="编辑止损价格" placement="top">
                        <IconButton
                          icon={<EditIcon />}
                          size="sm"
                          onClick={() => handleEdit(position)}
                          isDisabled={loading}
                          colorScheme="blue"
                          variant="ghost"
                          aria-label="编辑止损价格"
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        <Modal isOpen={isModalOpen} onClose={handleCloseModal} size="md">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              修改止损价格 - <Badge colorScheme="purple">{editingPosition?.symbol}</Badge>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>止损价格</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={editValues.first_stop_loss_price}
                    onChange={(e) =>
                      setEditValues({
                        ...editValues,
                        first_stop_loss_price: e.target.value
                      })
                    }
                    placeholder="请输入止损价格"
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleCloseModal}>
                取消
              </Button>
              <Button colorScheme="blue" onClick={handleSave} isLoading={loading}>
                保存
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default Positions; 